import { useEffect, useMemo, useRef, useState } from 'react';
import { canContractBillingProduct, isPendingRegistrationDataError } from '../../utils/integrationValidation';
import { getSelectedAccountInfo } from '../../utils/personAccessStorage';
import { useIntegrationData } from '../useIntegrationData/useIntegrationData';
import { usePersonAccess } from '../usePersonAccess';
export function useIntegrationValidation() {
    const selectedAccount = useMemo(() => getSelectedAccountInfo(), []);
    const [canContract, setCanContract] = useState(() => {
        if (!selectedAccount?.entityId) {
            return false;
        }
        return canContractBillingProduct(selectedAccount.entityId);
    });
    const hasInvalidIntegrationData = !canContract;
    const [isIntegrationAlertVisible, setIsIntegrationAlertVisible] = useState(false);
    const isAlertManuallyClosed = useRef(false);
    const [hasValidationFinished, setHasValidationFinished] = useState(false);
    const [hasValidationRequestError, setHasValidationRequestError] = useState(false);
    const { data: personAccessData, loading: isPersonAccessLoading, error: personAccessError } = usePersonAccess();
    const { validateActiveEntity, errors } = useIntegrationData(personAccessData ?? undefined, {
        enabled: false,
    });
    const lastValidatedEntityId = useRef(null);
    const hasCheckedAfterValidation = useRef(false);
    useEffect(() => {
        if (personAccessData &&
            personAccessData.length > 0 &&
            selectedAccount?.entityId &&
            lastValidatedEntityId.current !== selectedAccount.entityId) {
            setIsIntegrationAlertVisible(false);
            isAlertManuallyClosed.current = false;
            hasCheckedAfterValidation.current = false;
            setHasValidationFinished(false);
            setHasValidationRequestError(false);
            validateActiveEntity(selectedAccount.entityId)
                .then(() => {
                lastValidatedEntityId.current = selectedAccount.entityId ?? null;
                if (!isAlertManuallyClosed.current && selectedAccount?.entityId) {
                    const isValid = canContractBillingProduct(selectedAccount.entityId);
                    setIsIntegrationAlertVisible(!isValid);
                    setCanContract(isValid);
                    hasCheckedAfterValidation.current = true;
                }
                setHasValidationFinished(true);
            })
                .catch((error) => {
                const hasPendingRegistrationData = isPendingRegistrationDataError(error);
                if (hasPendingRegistrationData) {
                    lastValidatedEntityId.current = selectedAccount.entityId ?? null;
                }
                if (!isAlertManuallyClosed.current) {
                    setIsIntegrationAlertVisible(true);
                    hasCheckedAfterValidation.current = true;
                }
                setCanContract(false);
                setHasValidationFinished(true);
                setHasValidationRequestError(!hasPendingRegistrationData);
            });
        }
    }, [personAccessData, selectedAccount?.entityId]);
    useEffect(() => {
        if (!selectedAccount?.entityId || isAlertManuallyClosed.current || hasCheckedAfterValidation.current) {
            return;
        }
        const validationError = errors[selectedAccount.entityId];
        if (validationError) {
            setIsIntegrationAlertVisible(true);
            hasCheckedAfterValidation.current = true;
            setHasValidationFinished(true);
            setHasValidationRequestError(!isPendingRegistrationDataError(validationError));
        }
    }, [errors, selectedAccount?.entityId]);
    const handleCloseIntegrationAlert = () => {
        isAlertManuallyClosed.current = true;
        setIsIntegrationAlertVisible(false);
    };
    const isPersonAccessPending = isPersonAccessLoading || (!personAccessData && !personAccessError);
    const isValidationInFlight = !!personAccessData && personAccessData.length > 0 && !hasValidationFinished;
    const isValidatingIntegration = !!selectedAccount?.entityId && (isPersonAccessPending || isValidationInFlight);
    const hasIntegrationValidationError = hasValidationRequestError || (!!personAccessError && !personAccessData);
    return {
        canContract,
        hasInvalidIntegrationData,
        isValidatingIntegration,
        hasIntegrationValidationError,
        isIntegrationAlertVisible,
        handleCloseIntegrationAlert,
    };
}
